import { ArrowLeft20 } from '@carbon/icons-react';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  CpHeading03Primary,
  CpBody03Placeholder,
} from 'components/atoms/customer-portal/typography';
import { useGetBackground } from 'components/organisms/customer-portal/types/utils';
import { useIsResponsive, useIsTablet } from 'hooks/use-media-query';
import { CpSectionWrapper } from '../../common';
import {
  ControlButton,
  RightArrow,
} from '../../image-gallery/variants/image-gallery-carousel';
import { CpResourcesVariantWidgetProps } from '../types';
import {
  ResourceCard,
  ResourceCtaLink,
  ResourceDescription,
  ResourceImage,
  ResourceName,
} from './cp-resources-grid';

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cp-spacing-03);

  @media (max-width: 480px) {
    /* display: flex;
    flex-direction: column;
    gap: var(--cp-spacing-03); */
    /* margin-bottom: 0; */
  }
`;

const Title = styled(CpHeading03Primary)`
  margin-bottom: 0;
`;

const Subtitle = styled(CpBody03Placeholder)`
  margin-bottom: 0;
`;

export const CarouselControlWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-end;
  margin-right: 14rem;
  position: absolute;
  gap: 24px;
  width: 100%;
  /* z-index: 1;
  position: absolute;
  right: -120px; */

  @media (max-width: 768px) {
    /* top: 0px;
    left: 4px;
    gap: 24px; */
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-start;
  overflow: hidden;
  position: relative;
  gap: var(--cp-spacing-06);

  @media (max-width: 480px) {
    align-items: center;
  }

  .swiper {
    width: 100%;
    height: 50%;
    margin: 0;
  }

  .swiper-slide {
    text-align: center;
    font-size: 18px;
    background: #fff;

    /* Center slide text vertically */
    display: flex;
    justify-content: center;
    align-items: center;
    height: auto;
    width: 21.5rem !important;

    @media (max-width: 480px) {
      width: 100% !important;
    }
  }

  .swiper-pagination-bullets {
    margin-top: var(--cp-spacing-09);
    display: flex;
    justify-content: start;
    gap: var(--cp-spacing-03);
    visibility: hidden;

    @media (max-width: 480px) {
      margin-top: 0;
    }
  }

  .swiper-pagination-bullet {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--cp-background-as-black-opacity);
    cursor: pointer !important;
  }

  .swiper-pagination-bullet-active {
    background-color: black;
  }
`;

export const ResponsiveCarouselControlsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 24px;
`;

const TitleContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

export const CpResourcesScrollableGrid: React.FC<CpResourcesVariantWidgetProps> =
  ({ data, widget }) => {
    const { title, description } = data;
    const background = useGetBackground(widget);
    const isTablet = useIsTablet();
    const isResponsive = useIsResponsive();
    const slidesPerView = useMemo(
      () => (isResponsive ? 1 : isTablet ? 3 : 4),
      [isResponsive, isTablet],
    );
    const [swiperRef, setSwiperRef] = useState<any>();
    const [current, setCurrent] = useState(0);

    const handlePrevious = useCallback(() => {
      let slideTo;

      const totalSlides = data?.resources?.length;

      if (current - 1 === -1) {
        slideTo = totalSlides - 1;
      } else {
        slideTo = current - 1;
      }
      setCurrent(slideTo);

      swiperRef?.slideTo(slideTo);
    }, [current, data?.resources?.length, swiperRef]);

    const handleNext = useCallback(() => {
      let slideTo;
      const totalSlides = data?.resources?.length;

      if (current + 1 === totalSlides) {
        slideTo = 0;
      } else {
        slideTo = current + 1;
      }
      setCurrent(slideTo);
      swiperRef?.slideTo(slideTo);
    }, [current, data?.resources?.length, swiperRef]);

    return (
      <CpSectionWrapper
        background={background}
        paddingY={widget?.widgetDefinition?.styles?.paddingY as number}
        className="!cp-max-w-none"
      >
        <Wrapper>
          <TitleWrapper>
            <TitleContentWrapper>
              <Title autoAdjustColor>{title}</Title>
              <Subtitle autoAdjustColor>{description}</Subtitle>
            </TitleContentWrapper>
            {!isResponsive ? (
              <CarouselControlWrapper>
                <ControlButton
                  className="controlButton"
                  icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
                  onClick={handlePrevious}
                />
                <ControlButton
                  className="controlButton"
                  icon={<RightArrow className="cp-svg-auto-adjust-fill" />}
                  onClick={handleNext}
                />
              </CarouselControlWrapper>
            ) : (
              <></>
            )}
          </TitleWrapper>
          <Swiper
            freeMode
            onSwiper={setSwiperRef}
            pagination={{ clickable: true }}
            slidesPerView={slidesPerView}
            spaceBetween={24}
            modules={[Pagination]}
            className="mySwiper"
          >
            {data?.resources?.map((i) => {
              return (
                <SwiperSlide>
                  <ResourceCard>
                    <ResourceImage
                      src={i?.image?.attachment?.location}
                      alt={i?.name}
                    />
                    <ResourceName>{i?.title}</ResourceName>
                    <ResourceDescription>{i?.description}</ResourceDescription>
                    <ResourceCtaLink>
                      <a href={i?.cta}>{i?.cta}</a>
                    </ResourceCtaLink>
                  </ResourceCard>
                </SwiperSlide>
              );
            })}
          </Swiper>
          {isResponsive ? (
            <ResponsiveCarouselControlsWrapper>
              <ControlButton
                className="controlButton"
                icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
                onClick={handlePrevious}
              />
              <ControlButton
                className="controlButton"
                icon={<RightArrow className="cp-svg-auto-adjust-fill" />}
                onClick={handleNext}
              />
            </ResponsiveCarouselControlsWrapper>
          ) : (
            <></>
          )}
        </Wrapper>
      </CpSectionWrapper>
    );
  };
